import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

interface Role {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  color?: string;
  is_system: boolean;
  is_default: boolean;
  level: number;
  created_at: string;
}

interface Permission {
  id: string;
  resource: string;
  action: string;
  display_name: string;
  category: string;
}

interface PermissionGroup {
  category: string;
  permissions: Permission[];
}

export function RolesTab() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<PermissionGroup[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [rolePermissions, setRolePermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const [newRole, setNewRole] = useState({
    name: '',
    displayName: '',
    description: '',
    color: '#3b82f6',
    isDefault: false,
  });

  useEffect(() => {
    loadRoles();
    loadPermissions();
  }, []);

  async function loadRoles() {
    try {
      const response = await fetch('/admin/tenant/roles', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setRoles(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load roles:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadPermissions() {
    try {
      const response = await fetch('/admin/tenant/permissions', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setPermissions(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load permissions:', error);
    }
  }

  async function loadRolePermissions(roleId: string) {
    try {
      const response = await fetch(`/admin/tenant/roles/${roleId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setRolePermissions(data.data?.permissions || []);
      }
    } catch (error) {
      console.error('Failed to load role permissions:', error);
    }
  }

  async function handleCreateRole() {
    try {
      const response = await fetch('/admin/tenant/roles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify(newRole),
      });
      if (response.ok) {
        setIsCreateOpen(false);
        setNewRole({ name: '', displayName: '', description: '', color: '#3b82f6', isDefault: false });
        loadRoles();
      }
    } catch (error) {
      console.error('Failed to create role:', error);
    }
  }

  async function handleDeleteRole(roleId: string) {
    if (!confirm('Are you sure you want to delete this role?')) return;

    try {
      const response = await fetch(`/admin/tenant/roles/${roleId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (response.ok) {
        loadRoles();
        if (selectedRole?.id === roleId) {
          setSelectedRole(null);
        }
      }
    } catch (error) {
      console.error('Failed to delete role:', error);
    }
  }

  async function togglePermission(permissionId: string, isGranted: boolean) {
    if (!selectedRole) return;

    const method = isGranted ? 'DELETE' : 'POST';
    try {
      await fetch(`/admin/tenant/roles/${selectedRole.id}/permissions/${permissionId}`, {
        method,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      loadRolePermissions(selectedRole.id);
    } catch (error) {
      console.error('Failed to toggle permission:', error);
    }
  }

  function selectRole(role: Role) {
    setSelectedRole(role);
    loadRolePermissions(role.id);
  }

  const hasPermission = (permissionId: string) =>
    rolePermissions.some((p) => p.id === permissionId);

  return (
    <div className="grid gap-6 md:grid-cols-2">
      {/* Roles List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Roles</CardTitle>
              <CardDescription>Manage roles and their permissions</CardDescription>
            </div>
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button size="sm">Add Role</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Role</DialogTitle>
                  <DialogDescription>
                    Create a custom role with specific permissions.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">Name (unique identifier)</Label>
                    <Input
                      id="name"
                      placeholder="custom_role"
                      value={newRole.name}
                      onChange={(e) => setNewRole({ ...newRole, name: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="displayName">Display Name</Label>
                    <Input
                      id="displayName"
                      placeholder="Custom Role"
                      value={newRole.displayName}
                      onChange={(e) => setNewRole({ ...newRole, displayName: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      placeholder="Role description"
                      value={newRole.description}
                      onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="color">Color</Label>
                    <Input
                      id="color"
                      type="color"
                      value={newRole.color}
                      onChange={(e) => setNewRole({ ...newRole, color: e.target.value })}
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="isDefault"
                      checked={newRole.isDefault}
                      onCheckedChange={(checked) => setNewRole({ ...newRole, isDefault: checked })}
                    />
                    <Label htmlFor="isDefault">Default role for new users</Label>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateRole}>Create Role</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-muted-foreground">Loading roles...</div>
          ) : (
            <div className="space-y-2">
              {roles.map((role) => (
                <div
                  key={role.id}
                  className={`flex items-center justify-between rounded-lg border p-3 cursor-pointer transition-colors ${
                    selectedRole?.id === role.id ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                  }`}
                  onClick={() => selectRole(role)}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: role.color || '#6b7280' }}
                    />
                    <div>
                      <div className="font-medium">{role.display_name}</div>
                      <div className="text-sm text-muted-foreground">{role.description}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {role.is_system && <Badge variant="outline">System</Badge>}
                    {role.is_default && <Badge>Default</Badge>}
                    {!role.is_system && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRole(role.id);
                        }}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Permissions */}
      <Card>
        <CardHeader>
          <CardTitle>
            {selectedRole ? `Permissions for ${selectedRole.display_name}` : 'Permissions'}
          </CardTitle>
          <CardDescription>
            {selectedRole
              ? 'Toggle permissions for this role'
              : 'Select a role to manage its permissions'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!selectedRole ? (
            <div className="py-8 text-center text-muted-foreground">
              Select a role from the list to manage its permissions.
            </div>
          ) : selectedRole.is_system ? (
            <div className="py-8 text-center text-muted-foreground">
              System roles cannot be modified. They have predefined permissions.
            </div>
          ) : (
            <div className="space-y-6">
              {permissions.map((group) => (
                <div key={group.category}>
                  <h4 className="mb-2 font-medium capitalize">{group.category}</h4>
                  <div className="space-y-2">
                    {group.permissions.map((permission) => {
                      const isGranted = hasPermission(permission.id);
                      return (
                        <div
                          key={permission.id}
                          className="flex items-center justify-between rounded-lg border p-2"
                        >
                          <div>
                            <div className="text-sm font-medium">{permission.display_name}</div>
                            <div className="text-xs text-muted-foreground">
                              {permission.resource}:{permission.action}
                            </div>
                          </div>
                          <Switch
                            checked={isGranted}
                            onCheckedChange={() => togglePermission(permission.id, isGranted)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
